/**
 * Service to fetch CalEnviroScreen 4.0 (CES4) data from Supabase
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import { geofipsVariantsForEnergyFormat } from '../utils/energyFormatQuery';

export interface CES4Data {
  // Pollution metrics
  diesel_pm_percentile: number | null;
  pm2_5_percentile: number | null;
  ozone_percentile: number | null;
  tox_release_percentile: number | null;
  haz_waste_percentile: number | null;
  
  // Health metrics
  asthma_percentile: number | null;
  cardiovascular_disease_percentile: number | null;
  low_birth_weight_percentile: number | null;
  traffic_percentile: number | null;
}

/**
 * Fetch CES4 data for a location
 * @param locationName - Name of the location (city, county, or state)
 * @returns CES4 data with percentiles, or null if not found
 */
export async function fetchCES4Data(locationName: string): Promise<CES4Data | null> {
  try {
    // Get geofips for the location
    const geofips = await supabaseLocationService.getGeofips(locationName);
    if (!geofips) {
      return null;
    }

    // Try to find data by geofips and geography_type
    // First, determine geography type by querying population table
    const { data: locationData } = await supabaseClient
      .from('population')
      .select('geography_type')
      .eq('geofips', String(geofips))
      .limit(1)
      .maybeSingle();

    let geographyType = locationData?.geography_type || 'City';
    
    // If not found in population, try CES4 table directly with different geography types
    if (!locationData) {
      // Try City first, then County, then State
      const cityResult = await supabaseClient
        .from('calenviroscreen_4_0_aggregated')
        .select('geography_type')
        .eq('geofips', String(geofips))
        .eq('geography_type', 'City')
        .limit(1)
        .maybeSingle();
      
      if (cityResult.data) {
        geographyType = 'City';
      } else {
        const countyResult = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('geography_type')
          .eq('geofips', String(geofips))
          .eq('geography_type', 'County')
          .limit(1)
          .maybeSingle();
        
        if (countyResult.data) {
          geographyType = 'County';
        } else {
          geographyType = 'State';
        }
      }
    }

    const geofipsVariants = geofipsVariantsForEnergyFormat(geofips);

    // Fetch CES4 data (try padded/unpadded geofips — e.g. equity tables use 0617918 vs 617918)
    const { data, error } = await supabaseClient
      .from('calenviroscreen_4_0_aggregated')
      .select('diesel_pm_percentile, pm2_5_percentile, ozone_percentile, tox_release_percentile, haz_waste_percentile, asthma_percentile, cardiovascular_disease_percentile, low_birth_weight_percentile, traffic_percentile')
      .in('geofips', geofipsVariants)
      .eq('geography_type', geographyType)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(`CES4: Error fetching data for ${locationName}:`, error);
      return null;
    }

    if (!data) {
      return null;
    }

    return {
      diesel_pm_percentile: data.diesel_pm_percentile,
      pm2_5_percentile: data.pm2_5_percentile,
      ozone_percentile: data.ozone_percentile,
      tox_release_percentile: data.tox_release_percentile,
      haz_waste_percentile: data.haz_waste_percentile,
      asthma_percentile: data.asthma_percentile,
      cardiovascular_disease_percentile: data.cardiovascular_disease_percentile,
      low_birth_weight_percentile: data.low_birth_weight_percentile,
      traffic_percentile: data.traffic_percentile,
    };
  } catch (error) {
    console.error(`CES4: Exception fetching data for ${locationName}:`, error);
    return null;
  }
}

/**
 * Map CES4 indicator names to CES4 data fields
 */
export const CES4_INDICATOR_MAP: Record<string, keyof CES4Data> = {
  'Diesel PM': 'diesel_pm_percentile',
  'PM2.5': 'pm2_5_percentile',
  'Ozone': 'ozone_percentile',
  'Toxic Releases': 'tox_release_percentile',
  'Hazardous Waste': 'haz_waste_percentile',
  'Asthma Rate': 'asthma_percentile',
  'Cardiovascular Disease': 'cardiovascular_disease_percentile',
  'Low Birth Weight': 'low_birth_weight_percentile',
  'Traffic Density': 'traffic_percentile',
};

